import {React, _, observer, useLocalStore, observable, action, computed, classNames, toJS, trace, pluralize, useAsObservableSource, useEffect} from '@api'
import {Button, ButtonGroup, NumericInput, Spinner} from '@blueprintjs/core'
import {dataMapperConfig} from '@constants'
import {SpecificationItem, useVipLocalStore, VipLocalStore} from '@modules'
import {BOMRollups, SpecBOMCommentsCell} from './'
import {LoadingIndicator} from '@modules/common'
import {useCatalogItemSidebarDetailsStore, CatalogItemSidebarDetailsStore} from '@modules'
import {BillOfMaterialsConfig, deployments, nav, WorkspaceConfig, ICatalogFieldConfig, useCatalog, StoresInstance} from '@stores'
import {ListPreviewCard, DataMapper} from 'centric-ui-components'
import {Fragment, useContext} from 'react'
import * as css from './BillOfMaterialsUsageDetails.css'

class BillOfMaterialsUsageDetailsStore extends VipLocalStore {
  constructor(stores: StoresInstance,public props: { detailsStore: CatalogItemSidebarDetailsStore, workspace: string }) {
    super(stores)
    if (!props.workspace) {
      throw new Error(`workspace field is required for bom-usages renderer`)
    }

    if (!this.associatedWorkspace) {
      throw new Error(`Unable to find a workspace with id '${props.workspace}'`)
    }

    if (!this.associatedDetailView) {
      throw new Error(`No detail view is defined for workspace '${props.workspace}'`)
    }

    if (!this.associatedBillOfMaterialsConfig) {
      throw new Error(`No BOM sections are defined for workspace '${props.workspace}'`)
    }

    this.loadAssociatedCatalogItems()
  }

  get specData() { return this.props.detailsStore.data }

  @computed get associatedWorkspace(): WorkspaceConfig {
    const {workspaces, props: {workspace}} = this
    return workspaces.byId[workspace]
  }

  @computed get associatedDetailView() {
    return deployments.deploymentConfig.detailViews[this.associatedWorkspace.catalog.index]
  }

  @computed get associatedBillOfMaterialsConfig(): BillOfMaterialsConfig {
    const {associatedDetailView} = this
    const bomFields = _.chain(associatedDetailView.modal?.tabs?.reduce((acc, {fields, sections}) => {
      return [...acc, fields?.find(({bom}) => bom != null), sections?.map(s => (_.isFunction(s?.fields) ? s.fields(null, this.stores) : s?.fields)?.find(f => f.bom != null))]
    }, [])).flatten().compact().value()

    //console.log(toJS({associatedDetailView, bomFields}, {recurseEverything: true}))

    return _.first(bomFields)?.bom
  }

  @computed get catalogItemSpecFields(): Array<ICatalogFieldConfig> {
    const {associatedWorkspace, associatedBillOfMaterialsConfig: {specs}, catalog} = this
    return _.chain(specs).map(s => s.field).flatten().map(f => associatedWorkspace?.catalog?.fields?.find(cf => cf.field == f && cf.esIndex == catalog.index)).compact().uniq().value()
  }

  @computed get catalogItemSourceFields(): Array<string> {
    const {associatedBillOfMaterialsConfig: {specs}} = this
    return _.chain(specs).map(s => _.flatMap(s.rollups.map(r => r.localFields))).flatten().compact().uniq().value()
  }

  @computed get catalogItemDataMap() {
    const {associatedBillOfMaterialsConfig: {specs}, props: {}} = this
    //console.log(toJS(associatedBillOfMaterialsConfig))
    return {
      ...dataMapperConfig.specDataMapperConfig,
      ...this.associatedWorkspace.dataMap
    }
  }

  @action loadAssociatedCatalogItems = async () => {
    try {
      const {es, associatedWorkspace, catalogItemSpecFields, catalogItemSourceFields, catalogItemDataMap, props: {detailsStore: {data: {id}}}, catalog: {catalogItemName}} = this
      if(!id) return;
      this.loading = true
      const {items} = es.unwrapHitsResult(
        await es.search({
          index: associatedWorkspace.catalog.index,
          meta: `Load associated catalog item BOM values for ${catalogItemName} '${id}'`,
          body: {
            size: 10000,
            query: {
              bool: {
                should: catalogItemSpecFields.map(f => ({term: {[`${f.field}.id`]: id}})),
                minimum_should_match: 1
              }
            },
            sort: [{[associatedWorkspace.dataMap?.title ?? 'name']: {'order': 'asc'}}],
            _source: [
              ...catalogItemSpecFields.map(s => `${s.field}.*`),
              ...catalogItemSourceFields,
              'billOfMaterials',
              ..._.values(catalogItemDataMap)
            ].filter(_.identity)
          }
        }, {log: {query: false, result: false}})
      )

      this.associatedItems = items
    } finally {
      this.loading = false
    }
  }

  @observable loading = true
  @observable.ref associatedItems: Array<any> = []

  @computed get totals() {
    const {associatedItems, itemStores} = this
    return (
      associatedItems.reduce((acc, { }, i) => {
        const store = itemStores[i]
        if (!store) return acc

        const { bomQuantities, catalogItemValues, rollupTotals  } = store

        //console.log(toJS({acc, rollupTotals}, {recurseEverything: true}))
        return ({
          ...acc,
          specUsages: {...acc.specUsages, ...bomQuantities.reduce((quantityAcc, {field, quantity}) => ({...quantityAcc, [field]: (quantity ?? 0) + (acc.specUsages[field] ?? 0)}), {})},
          catalogItemUsages: {...acc.catalogItemUsages, ...catalogItemValues.reduce((quantityAcc, {field, value}) => ({...quantityAcc, [field]: (value ?? 0) + (acc.catalogItemUsages[field] ?? 0)}), {})},
          rollups: {
            ...acc.rollups,
            ...rollupTotals.reduce(
              (rollupTotalsAcc, {rollupResults}) => ({
                ...rollupTotalsAcc, ...rollupResults.filter(def => !def.def.localOnly).reduce(
                  (rollupAcc, {def, result}) => ({...rollupAcc, [def.label]: (result ?? 0) + (acc.rollups[def.label] ?? 0)}),
                  {})
              }),
              {})
          }
        })
      }, {
        specUsages: {},
        catalogItemUsages: {},
        rollups: {}
      }))
  }

  @observable itemStores: {[index: number]: AssociatedItemRowStore} = {}

  @computed get isDirty() {
    return _.values(this.itemStores).some(s => s.isDirty)
  }

  @action revertChanges = () => {
    const {itemStores} = this
    _.values(itemStores).forEach(s => s.revertChanges())
  }

  @action applyChanges = async () => {
    const {itemStores} = this
    await Promise.all(_.values(itemStores).map(s => s.applyChanges()))
  }
}

export const BillOfMaterialsUsageDetailsStoreContext = React.createContext<BillOfMaterialsUsageDetailsStore>(undefined)
export const useBillOfMaterialsUsageDetailsStore = () => useContext(BillOfMaterialsUsageDetailsStoreContext)

export const BillOfMaterialsUsageDetails = observer<{ className?: string, workspace: string }>(({className, workspace}) => {
  const detailsStore = useCatalogItemSidebarDetailsStore(),
    obsProps = useAsObservableSource({detailsStore, workspace}),
    store = useVipLocalStore((ws) => new BillOfMaterialsUsageDetailsStore(ws, obsProps)),
    {loading, associatedItems} = store

  //console.log(toJS({specs, localFields}, {recurseEverything: true}))

  return (
    <BillOfMaterialsUsageDetailsStoreContext.Provider value={store}>
      <div className={classNames(css.root, className)}
           data-loading={loading}>

        <div className={css.grid}
             data-rows={associatedItems?.length ?? 0}
             style={{'--rows': associatedItems?.length ?? 0} as any}>
          <ColumnHeaders/>
          <LoadingIndicator className={css.loading}/>
          <AssociatedItemRows/>
          {associatedItems.length ? <TotalsFooter /> : null}
        </div>
      </div>
    </BillOfMaterialsUsageDetailsStoreContext.Provider>
  )
})

const ColumnHeaders = observer(() => {
  const {associatedWorkspace: {itemName}, associatedItems, loading} = useBillOfMaterialsUsageDetailsStore()

  return (
    <Fragment>
      <div data-type='ColumnHeader' className={css.catalogItem}>{pluralize(itemName)}{!loading && ` (${associatedItems.length.toLocaleString('en-us')})`}{false && loading && <Spinner size={20} className={css.loadingAssociatedItems}/>}</div>
      <div data-type='ColumnHeader' className={css.usages}>Usage</div>
      <div data-type='ColumnHeader' className={css.targets}>Target Quantity</div>
      <div data-type='ColumnHeader' className={css.rollups}>Rollup Totals</div>
      <div data-type='ColumnHeader' className={css.notes}>Comments</div>
      {/*<div data-type='ColumnHeader' className={css.actions}>Actions</div>*/}
    </Fragment>
  )
})

const TotalsFooter = observer(function TotalsFooter() {
  const {strings, catalog, isDirty, revertChanges, applyChanges, totals: {specUsages, catalogItemUsages, rollups}, associatedWorkspace} = useBillOfMaterialsUsageDetailsStore()

  //console.log(toJS({rollups, specUsages, catalogItemUsages}, { recurseEverything: true}))
  return (
    <Fragment>
    <div data-dirty={isDirty} data-type='FooterCell' className={css.catalogItem}>
       <div className={css.buttons}>
        <Button intent='none' text='Revert' onClick={revertChanges} />
	      <Button intent='primary' text='Apply Changes' onClick={applyChanges}/>
      </div>
    </div>

    <div data-type='FooterCell' className={css.usages}>
      {_.keys(specUsages).map(field => {
        const {label} = catalog.fieldForWorkspace(field, associatedWorkspace),
          total = specUsages[field]

        return <div key={field} className={css.quantityTotal}>
          <label>{label}</label>
          <NumericInput
            className={css.numericInput}
            allowNumericCharactersOnly
            selectAllOnFocus
            min={0}
            value={_.isNaN(total) ? 0 : total}
            placeholder={strings.noValue}
            readOnly
          />
        </div>
      })}
    </div>
      <div data-type='FooterCell' className={css.targets}>
        {_.keys(catalogItemUsages).map(field => {
          const {label} = catalog.fieldForWorkspace(field, associatedWorkspace),
            total = catalogItemUsages[field]

          return <div key={field} className={css.quantityTotal}>
            <label>{label}</label>
            <NumericInput
              className={css.numericInput}
              allowNumericCharactersOnly
              selectAllOnFocus
              min={0}
              value={_.isNaN(total) ? 0 : total}
              placeholder={strings.noValue}
              readOnly
            />
          </div>
        })}
      </div>
      <div data-type='FooterCell' className={css.rollups}>
        {_.keys(rollups).map((label, i) => <div className={css.rollup} key={i}>
          <label>{label}</label>
          <div className={css.value}>{rollups[label].toLocaleString()}</div>
        </div>)}
      </div>
      <div data-type='FooterCell' className={css.notes} />
    </Fragment>
  )
})

//AssociatedItemRows

const AssociatedItemRows = observer(() => {
  const {associatedItems, loading} = useBillOfMaterialsUsageDetailsStore()

  return (
    <Fragment>
      {_.isEmpty(associatedItems) && !loading
       ? <div className={css.noResults}>No results found.</div>
       : associatedItems.map((item, i) => <AssociatedItemRow key={i} index={i} data={item}/>)}
    </Fragment>
  )
})

class AssociatedItemRowStore extends VipLocalStore {
  constructor(stores: StoresInstance,public props: { index: number, bomStore: BillOfMaterialsUsageDetailsStore }, data: any) {
    super(stores)
    const {index, bomStore} = props
    bomStore.itemStores[index] = this
    this.originalData = data
    this.data = data
  }

  @observable data : any
  @observable.ref originalData : any

  @computed get isLastRow() {
    return this.props.index + 1 == this.props.bomStore.associatedItems.length
  }

  @action ignoreHyperlinkClick = (e: React.MouseEvent) => {
    e.preventDefault()
  }

  @computed get specUrl() {
    const {data, props: {bomStore: {associatedWorkspace}}, catalog} = this
    return !data || !associatedWorkspace
           ? null
           : null  //nav.urlFor_string({workspace: associatedWorkspace, queryParams: {...catalog.sidebar.queryParamsFor({right: {open: true, path: [{where: [{value: catalog.idFor(data)}]}]}})}})
  }

  @computed get rollupTotals() {
    const {catalog, bomFieldsForDetailSpec, data, data: {billOfMaterials}, props: {bomStore: {catalogItemSpecFields, associatedBillOfMaterialsConfig: {specs}, specData}}} = this
    return catalogItemSpecFields
      .filter(f => bomFieldsForDetailSpec.find(dsf => dsf.field == f.field))
      .map(f => {
      const quantity = billOfMaterials?.find(({field}) => field == f.field)?.quantity,
        specBOMConfig = specs.find(s => s.field == f.field),
        bomFieldValues = specBOMConfig.bomFields.map(bomField => ({field: bomField, value: _.get(specData, bomField)})),
        bomFieldsByName = bomFieldValues.reduce((acc, v) => ({...acc, [v.field]: v.value}), {})

      return {
        field: f,
        specBOMConfig,
        quantity,
        rollupResults: !specBOMConfig?.rollups
                       ? null
                       : specBOMConfig.rollups.map(rollupDef => quantity == null ? {def: rollupDef, result: null, quantity} : catalog.calculateRollup({data, spec: specData, rollupDef, quantity, bomFieldsByName}))
      }
    })
  }

  @computed get bomFieldsForDetailSpec() {
    const {data, props: {bomStore: {specData, associatedBillOfMaterialsConfig}}} = this
    return associatedBillOfMaterialsConfig.specs.filter(({field}) => _.get(data, field)?.id == specData.id)
  }

  @computed get bomQuantities() {
    const {calcBomQuantities, data} = this
    return calcBomQuantities(data)
  }

  private calcBomQuantities = (data) => {
    const {bomFieldsForDetailSpec} = this
    return bomFieldsForDetailSpec.map(f => {
      const bom = data.billOfMaterials?.find(bom => bom.field == f.field)
      return {field: f.field, quantity: bom?.quantity}
    })
  }

  @computed get originalBomQuantities() {
    const {calcBomQuantities, originalData} = this
    return calcBomQuantities(originalData)
  }

  @computed get isBomQuantityDirty() {
    const {originalBomQuantities, bomQuantities} = this
    return !_.isEqual(originalBomQuantities, bomQuantities)
  }

  @computed get catalogItemValues() {
    const {data, calcCatalogItemValues} = this
    return calcCatalogItemValues(data)
  }

  @computed get originalCatalogItemValues() {
    const {originalData, calcCatalogItemValues} = this
    return calcCatalogItemValues(originalData)
  }

  @computed get isCatalogItemValuesDirty() {
    const {originalCatalogItemValues, catalogItemValues} = this
    return !_.isEqual(originalCatalogItemValues, catalogItemValues)
  }

  private calcCatalogItemValues = (data) => {
    const {props: {bomStore: {catalogItemSourceFields}}} = this
    return catalogItemSourceFields.map(field => {
      const value = _.get(data, field)
      return {field, value}
    })
  }

  @computed get isDirty() {
    const {data, originalData} = this
    //console.log(toJS({data, originalData}, {recurseEverything: true}))
    return !_.isEqual(this.data, this.originalData)
  }

  @action onChangeCatalogItemValue = (field) => (value) => {
    _.set(this.data, field, value)
  }

  @action onChangeBomQuantity = (field) => (value) => {
    const {data} = this
    if (!data.billOfMaterials) {
      data.billOfMaterials = []
    }
    const {billOfMaterials} = data
    const bom = billOfMaterials.find(f => f.field === field)
    if (!bom) {
      billOfMaterials.push({field, quantity: value})
    }
    else {
      bom.quantity = value
    }
    //console.log(field, value)
  }

  @observable comments = Array.from({length: _.random(0, 5)})

  @action applyChanges = async () => {
    const {data, es, isDirty, isCatalogItemValuesDirty, isBomQuantityDirty, props: {bomStore: {associatedWorkspace}}} = this

    if (isDirty) {
      this.originalData = this.data
      // Todo - this is inefficient and should go through API
      // We shouldn't need to update the ENTIRE document, just the modified bill of materials/target quantity fields
      return await es.updateDocument({
        index: associatedWorkspace.catalog.index,
        id: data._id,
        doc: {
          ..._.omit(data, '_id')
        }
      })
    }
  }

  @action revertChanges = () => {
    this.data = this.originalData
  }

}

const AssociatedItemRow = observer<{ index: number, data: any }>(({index, data}) => {
  const bomStore = useBillOfMaterialsUsageDetailsStore(),
    obsProps = useAsObservableSource({bomStore, index}),
    {
      strings, isBomQuantityDirty,
      isCatalogItemValuesDirty,
      comments,
      onChangeCatalogItemValue,
      onChangeBomQuantity,
      ignoreHyperlinkClick,
      bomQuantities,
      isLastRow,
      bomFieldsForDetailSpec,
      specUrl,
      rollupTotals,
      catalogItemValues, catalog, stores, workspaces,
      props: {bomStore: {catalogItemDataMap, associatedWorkspace, props: {detailsStore: {data: specData}}, catalogItemSpecFields, catalogItemSourceFields, associatedBillOfMaterialsConfig}}
    } = useVipLocalStore(
      (ws) => new AssociatedItemRowStore(ws, obsProps, data)),
    style: any = {'--index': index}

//  const extra = associatedBillOfMaterialsConfig.specs.reduce((acc, f, i) => ({...acc, [`subtitle${i + 2}`]: `${f.field}.name`}), {})
  const extra = (
    <div className={css.extraSpecFields}>
      {bomFieldsForDetailSpec.map((config, i) => {
          const fieldInProductWorkspace = catalog.fieldForWorkspace(config.field, associatedWorkspace),
            fieldWorkspace = workspaces.byIndexScalar[fieldInProductWorkspace.esIndex]

          let value = _.get(data, config.field)

          //console.log({data, config, value, specData})

          // TODO This is because for the demo the data attached to the product subfield DOES NOT MATCH in all cases the data in the underlying material index (data inconsistency!)
          if (value?.id == specData._id) { value = specData}

          const valueText = _.get(value, fieldWorkspace.dataMap?.title ?? 'name') ?? strings.noValue

          return <SpecificationItem key={i}
                                    className={css.specItem}
                                    noPopover
                                    field={fieldInProductWorkspace}
                                    valueComponent={valueText}
                                    showTitle
                                    title={fieldInProductWorkspace.label}
                                    info={value}
          />
        })}

    </div>
  )

  //console.log({rollupTotals, data, catalogItemSourceFields, catalogItemSpecFields})
  return (
    <Fragment>
      <div data-type='cell' className={classNames(isLastRow && css.last, css.catalogItem)} style={style}>
        <a href={specUrl} target='_blank' onClick={ignoreHyperlinkClick}>
          <DataMapper data={data} config={{...catalogItemDataMap, subtitle1: null}} nullValue={strings.noValue}>
            <ListPreviewCard stores={stores} hideTooltips className={css.previewCard} showImage extra={extra}/>
          </DataMapper>
        </a>
      </div>
      <div data-type='cell' data-dirty={isBomQuantityDirty} className={classNames(isLastRow && css.last, css.usages)} style={style}>
        {bomQuantities.map(({field, quantity}) => {
          //  console.log(bom)
          return (
            <div className={css.quantityItem} key={field}>
              <NumericInput
                className={css.numericInput}
                allowNumericCharactersOnly
                selectAllOnFocus
                min={0}
                value={quantity ?? undefined}
                placeholder={strings.noValue}
                onValueChange={onChangeBomQuantity(field)}
              />
            </div>
          )
        })}
      </div>

      <div data-type='cell' data-dirty={isCatalogItemValuesDirty} className={classNames(isLastRow && css.last, css.targets)} style={style}>
        {catalogItemValues.map(({field, value}) => {
          return <div key={field} className={css.quantityItem}>
            <NumericInput
              className={css.numericInput}
              allowNumericCharactersOnly
              selectAllOnFocus
              min={0}
              value={_.isNaN(value) ? 0 : value}
              placeholder={strings.noValue}
              onValueChange={onChangeCatalogItemValue(field)}
            />
          </div>
        })}

      </div>

      <div data-type='cell' className={classNames(isLastRow && css.last, css.rollups)} style={style}>
        {rollupTotals?.map(({specBOMConfig, rollupResults}) =>
          <div className={css.rollupGroup} key={specBOMConfig.field}>
            <BOMRollups rollupResults={rollupResults}/>
          </div>)}
      </div>

      <div data-type='cell' className={classNames(isLastRow && css.last, css.notes)} style={style}>
        <SpecBOMCommentsCell comments={comments}/>
      </div>

      {/*<div data-type='cell' className={classNames(isLastRow && css.last, css.actions)} style={style}>*/}
      {/*</div>*/}

      </Fragment>
  )
})


// WEBPACK FOOTER //
// ./src/modules/CatalogItemDetails/CatalogItemSidebarDetails/FieldRenderers/BillOfMaterials/BillOfMaterialsUsageDetails.tsx